import type { ErrorResult, Result, SuccessResult } from "./types";
/**
 * Creates a new error result with the specified error details
 * @param code - Error code (e.g., "NOT_FOUND", 404)
 * @param message - Human-readable error message
 * @param detail - Technical error details for developers
 * @returns An ErrorResult object
 */
export function newErrorResult(
  code: string | number = "UNKNOWN_ERROR",
  message = "An unexpected error has occurred",
  detail = "An unknown error occurred",
): ErrorResult {
  return {
    code,
    detail,
    message,
    isOk: false,
    isError: true,
  };
}

// newDoubleFalseResultError returns an error indicating both the isOk and
// isError flags were false when working with a result. This is a condition
// which should never actually happen. The error is always in English.
export function newDoubleFalseResultError(caller: string): ErrorResult {
  return newErrorResult("DOUBLE_FALSE", "Double false result error", caller);
}

/**
 * Creates a new success result with the specified data and details
 * @param data - The successful operation result data
 * @param code - Success code (e.g., "SUCCESS", 200)
 * @param message - Human-readable success message
 * @param detail - Technical details about the operation
 * @returns A SuccessResult object
 */
export function newSuccessResult<T>(
  data: T,
  code: string | number = "SUCCESS",
  message = "Operation completed successfully",
  detail = "Operation completed successfully",
): SuccessResult<T> {
  return {
    code,
    detail,
    message,
    data,
    isOk: true,
    isError: false,
  };
}

/**
 * Checks if a result is a success result
 * @param result - The result to check
 * @returns true if the result is a success result
 */
export function isSuccess<T>(result: Result<T>): result is SuccessResult<T> {
  return result.isOk;
}

/**
 * Checks if a result is an error result
 * @param result - The result to check
 * @returns true if the result is an error result
 */
export function isError<T>(result: Result<T>): result is ErrorResult {
  return result.isError;
}

/**
 * Maps a success result to a new success result with transformed data
 * @param result - The result to map
 * @param fn - The function to transform the data
 * @returns A new result with transformed data if successful, or the original error result
 */
export function mapResult<T, U>(result: Result<T>, fn: (data: T) => U): Result<U> {
  if (isSuccess(result)) {
    return newSuccessResult(fn(result.data), result.code, result.message, result.detail);
  }
  return result;
}

/**
 * Maps an error result to a new error result with transformed error details
 * @param result - The result to map
 * @param fn - The function to transform the error details
 * @returns A new error result with transformed details if error, or the original success result
 */
export function mapError<T>(result: Result<T>, fn: (error: ErrorResult) => ErrorResult): Result<T> {
  if (isError(result)) {
    return fn(result);
  }
  return result;
}

/**
 * Unwraps a result, returning the data if successful or throwing an error if not
 * @param result - The result to unwrap
 * @returns The data if the result is successful
 * @throws Error if the result is an error
 */
export function unwrapResult<T>(result: Result<T>): T {
  if (isSuccess(result)) {
    return result.data;
  }
  throw new Error(`${result.code}: ${result.message}`);
}

/**
 * Unwraps a result, returning the data if successful or a default value if not
 * @param result - The result to unwrap
 * @param defaultValue - The default value to return if the result is an error
 * @returns The data if successful, or the default value if not
 */
export function unwrapResultOr<T>(result: Result<T>, defaultValue: T): T {
  if (isSuccess(result)) {
    return result.data;
  }
  return defaultValue;
}
